import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Play,
  CheckCircle,
  Clock,
  Building2,
  ChevronRight,
  Target,
  Zap,
  Crown,
  Star,
  ArrowLeft,
  Sparkles,
  Check
} from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssessmentsPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [assessments, setAssessments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [step, setStep] = useState(1); // 1: company selection, 2: tier selection
  
  useEffect(() => {
    fetchData();
    
    // Check if coming from company page
    const companyId = searchParams.get('company');
    if (companyId) {
      setSelectedCompany(companyId);
      setShowNewForm(true);
    }
  }, [searchParams]);
  
  const fetchData = async () => {
    try {
      const [assessmentsRes, companiesRes, tiersRes] = await Promise.all([
        axios.get(`${API}/assessments`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/assessment-tiers`)
      ]);
      setAssessments(assessmentsRes.data);
      setCompanies(companiesRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateAssessment = async () => {
    if (!selectedCompany || !selectedTier) return;
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/assessments`, {
        company_id: selectedCompany,
        assessment_tier: selectedTier
      });
      
      navigate(`/assessments/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          language === 'tr' 
            ? 'Bu paket için aktif abonelik gerekli. Lütfen önce bir plan satın alın.' 
            : 'Active subscription required for this tier. Please purchase a plan first.',
          { action: { label: language === 'tr' ? 'Paketlere Git' : 'View Plans', onClick: () => navigate('/pricing') } }
        );
      } else {
        toast.error(language === 'tr' ? 'Değerlendirme oluşturulamadı' : 'Failed to create assessment');
      }
      console.error('Error creating assessment:', error);
    } finally {
      setCreating(false);
    }
  };
  
  const handleCompanyNext = () => {
    if (selectedCompany) {
      setStep(2);
    }
  };
  
  const handleBack = () => {
    setStep(1);
    setSelectedTier('');
  };
  
  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'executive_scan':
        return <Zap className="h-6 w-6" />;
      case 'operational':
        return <Target className="h-6 w-6" />;
      case 'enterprise':
        return <Crown className="h-6 w-6" />;
      case 'strategic':
        return <Star className="h-6 w-6" />;
      default:
        return <Target className="h-6 w-6" />;
    }
  };
  
  const getTierGradient = (tierId) => {
    switch (tierId) {
      case 'executive_scan':
        return 'from-slate-500 to-slate-600';
      case 'operational':
        return 'from-blue-500 to-blue-600';
      case 'enterprise':
        return 'from-purple-500 to-purple-600';
      case 'strategic':
        return 'from-amber-500 to-amber-600';
      default:
        return 'from-blue-500 to-blue-600';
    }
  };
  
  const getStatusBadge = (status) => {
    if (status === 'completed') {
      return (
        <span className="flex items-center space-x-1 px-2 py-1 text-xs rounded-full bg-emerald-500/10 text-emerald-400">
          <CheckCircle size={12} />
          <span>{language === 'tr' ? 'Tamamlandı' : 'Completed'}</span>
        </span>
      );
    }
    return (
      <span className="flex items-center space-x-1 px-2 py-1 text-xs rounded-full bg-amber-500/10 text-amber-400">
        <Clock size={12} />
        <span>{language === 'tr' ? 'Devam Ediyor' : 'In Progress'}</span>
      </span>
    );
  };
  
  const getTierBadge = (tierId) => {
    const tierNames = {
      executive_scan: { tr: 'Yönetici', en: 'Executive' },
      operational: { tr: 'Operasyonel', en: 'Operational' },
      enterprise: { tr: 'Kurumsal', en: 'Enterprise' },
      strategic: { tr: 'Stratejik', en: 'Strategic' }
    };
    const name = tierNames[tierId]?.[language] || tierId;
    return (
      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
        {name}
      </span>
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-white">{t('loading')}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="assessments-page">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('assessments')}</h1>
            <p className="text-slate-400">
              {language === 'tr' 
                ? 'Dijital olgunluk değerlendirmelerinizi yönetin'
                : 'Manage your digital maturity assessments'}
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setShowNewForm(true);
              setStep(1);
              setSelectedTier('');
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-assessment-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('newAssessment')}
          </Button>
        </div>
        
        {/* New Assessment Form */}
        {showNewForm && (
          <Card className="bg-slate-900/50 border-slate-800 mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">{t('startNewAssessment')}</CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    {step === 1 
                      ? (language === 'tr' ? 'Adım 1: Şirket seçimi' : 'Step 1: Select company')
                      : (language === 'tr' ? 'Adım 2: Değerlendirme paketi seçimi' : 'Step 2: Select assessment package')
                    }
                  </CardDescription>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-blue-500' : 'bg-slate-700'}`} />
                  <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-blue-500' : 'bg-slate-700'}`} />
                  <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-blue-500' : 'bg-slate-700'}`} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {companies.length > 0 ? (
                <>
                  {/* Step 1: Company Selection */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-slate-400 mb-2 block">{t('selectCompany')}</label>
                        <Select
                          value={selectedCompany}
                          onValueChange={setSelectedCompany}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="company-select">
                            <SelectValue placeholder={language === 'tr' ? 'Şirket seçin' : 'Select company'} />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {companies.map((company) => (
                              <SelectItem 
                                key={company.id} 
                                value={company.id}
                                className="text-white hover:bg-slate-700"
                              >
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex justify-end space-x-3">
                        <Button
                          variant="outline"
                          onClick={() => setShowNewForm(false)}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          {t('cancel')}
                        </Button>
                        <Button
                          onClick={handleCompanyNext}
                          disabled={!selectedCompany}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="next-step-btn"
                        >
                          {language === 'tr' ? 'Devam' : 'Continue'}
                          <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Step 2: Tier Selection */}
                  {step === 2 && (
                    <div className="space-y-6">
                      {/* AI Recommendation Banner */}
                      <div className="p-4 rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20">
                        <div className="flex items-start space-x-3">
                          <div className="p-2 rounded-lg bg-purple-500/20">
                            <Sparkles className="h-5 w-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-purple-300">
                              {language === 'tr' ? 'AI Önerisi' : 'AI Recommendation'}
                            </p>
                            <p className="text-sm text-slate-400 mt-1">
                              {language === 'tr' 
                                ? 'Şirketinizin büyüklüğüne göre "Kurumsal Değerlendirme" paketi önerilmektedir.'
                                : 'Based on your company size, "Enterprise Assessment" package is recommended.'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Tier Cards */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {tiers.map((tier) => (
                          <div
                            key={tier.id}
                            onClick={() => setSelectedTier(tier.id)}
                            className={`relative cursor-pointer rounded-xl border-2 p-5 transition-all ${
                              selectedTier === tier.id
                                ? 'border-blue-500 bg-blue-500/5'
                                : 'border-slate-700 hover:border-slate-600 bg-slate-800/30'
                            }`}
                            data-testid={`tier-${tier.id}`}
                          >
                            {/* Badge */}
                            {tier.badge && (
                              <div className={`absolute -top-3 right-4 px-3 py-1 rounded-full text-xs font-medium ${
                                tier.badge === 'most_popular' 
                                  ? 'bg-purple-500 text-white' 
                                  : 'bg-amber-500 text-white'
                              }`}>
                                {tier.badge === 'most_popular' 
                                  ? (language === 'tr' ? 'En Popüler' : 'Most Popular')
                                  : (language === 'tr' ? 'Tam Kapsamlı' : 'Full Coverage')}
                              </div>
                            )}
                            
                            {/* Selection indicator */}
                            {selectedTier === tier.id && (
                              <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            )}
                            
                            <div className="flex items-start space-x-4">
                              <div className={`p-3 rounded-lg bg-gradient-to-br ${getTierGradient(tier.id)} text-white`}>
                                {getTierIcon(tier.id)}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-white">
                                  {language === 'tr' ? tier.name_tr : tier.name_en}
                                </h3>
                                <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                                  {language === 'tr' ? tier.description_tr : tier.description_en}
                                </p>
                              </div>
                            </div>
                            
                            <div className="mt-4 flex items-center justify-between text-sm">
                              <span className="text-slate-400">
                                {tier.question_count[0]}-{tier.question_count[1]} {language === 'tr' ? 'soru' : 'questions'}
                              </span>
                              <span className="text-slate-400">
                                ~{tier.duration_minutes[0]}-{tier.duration_minutes[1]} {language === 'tr' ? 'dk' : 'min'}
                              </span>
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-slate-700/50">
                              <ul className="space-y-2">
                                {(language === 'tr' ? tier.features_tr : tier.features_en).slice(0, 3).map((feature, i) => (
                                  <li key={i} className="flex items-center text-sm text-slate-300">
                                    <CheckCircle className="h-4 w-4 text-emerald-400 mr-2 flex-shrink-0" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex justify-between">
                        <Button
                          variant="outline"
                          onClick={handleBack}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          {language === 'tr' ? 'Geri' : 'Back'}
                        </Button>
                        
                        <Button
                          onClick={handleCreateAssessment}
                          disabled={!selectedTier || creating}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="start-assessment-btn"
                        >
                          {creating ? t('loading') : (
                            <>
                              <Play className="mr-2 h-4 w-4" />
                              {language === 'tr' ? 'Değerlendirmeyi Başlat' : 'Start Assessment'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-slate-400 mb-4">
                    {language === 'tr' 
                      ? 'Önce bir şirket eklemeniz gerekiyor'
                      : 'You need to add a company first'}
                  </p>
                  <Link to="/companies">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Building2 className="mr-2 h-4 w-4" />
                      {t('addCompany')}
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Assessments List */}
        {assessments.length > 0 ? (
          <div className="space-y-4">
            {assessments.map((assessment) => (
              <Card 
                key={assessment.id}
                className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors"
                data-testid={`assessment-card-${assessment.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400">
                        <Target size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{assessment.company_name}</h3>
                        <div className="flex items-center space-x-3 mt-1">
                          {assessment.owner_name && (
                            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                              {assessment.owner_name}
                            </span>
                          )}
                          {getStatusBadge(assessment.status)}
                          {assessment.assessment_tier && getTierBadge(assessment.assessment_tier)}
                          {assessment.gdmii_score && (
                            <span className="text-sm text-slate-400">
                              GDMII: <span className="text-white font-medium">{assessment.gdmii_score.toFixed(1)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <Link to={`/assessments/${assessment.id}`}>
                      <Button 
                        variant="outline"
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid={`view-assessment-${assessment.id}`}
                      >
                        {assessment.status === 'completed' ? t('view') : t('continueAssessment')}
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                  
                  {assessment.status === 'in_progress' && (
                    <div className="mt-4 pt-4 border-t border-slate-800">
                      <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                        <span>{language === 'tr' ? 'İlerleme' : 'Progress'}</span>
                        <span>
                          {assessment.total_questions > 0 
                            ? `${assessment.total_questions} ${language === 'tr' ? 'soru' : 'questions'}`
                            : `${language === 'tr' ? 'Mevcut alan' : 'Current pillar'}: ${assessment.current_pillar}`
                          }
                        </span>
                      </div>
                      <Progress value={30} className="h-2 bg-slate-800" />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-16 text-center">
              <Target className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {language === 'tr' ? 'Henüz değerlendirme yok' : 'No assessments yet'}
              </h3>
              <p className="text-slate-400 mb-6">
                {language === 'tr' 
                  ? 'İlk dijital olgunluk değerlendirmenizi başlatın'
                  : 'Start your first digital maturity assessment'}
              </p>
              <Button 
                onClick={() => setShowNewForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('startNewAssessment')}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AssessmentsPage;
